"use client";

import { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  FaUsers,
  FaPlus,
  FaTrash,
  FaEdit,
  FaImage,
  FaUser,
  FaBriefcase,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
  FaEye,
  FaUndo,
  FaSave,
  FaGripVertical,
  FaArrowUp,
  FaArrowDown,
  FaCalendarAlt,
} from "react-icons/fa";

const ECUpdate = () => {
  const [members, setMembers] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [availableYears, setAvailableYears] = useState([]);
  const [form, setForm] = useState({
    name: "",
    position: "",
    photoUrl: "",
    category: "board",
  });
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [isReordering, setIsReordering] = useState(false);

  const categories = [
    {
      id: "board",
      name: "Board Members",
      color: "from-purple-500 to-purple-700",
    },
    {
      id: "associates",
      name: "Associates",
      color: "from-blue-500 to-blue-700",
    },
    { id: "heads", name: "Heads", color: "from-green-500 to-green-700" },
    {
      id: "co-heads",
      name: "Co-Heads",
      color: "from-orange-500 to-orange-700",
    },
  ];

  // Generate current academic year (e.g., "2024-2025")
  const getCurrentAcademicYear = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // JavaScript months are 0-indexed

    // Academic year typically starts in July/August
    if (currentMonth >= 7) {
      return `${currentYear}-${currentYear + 1}`;
    } else {
      return `${currentYear - 1}-${currentYear}`;
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1 }
    );

    document
      .querySelectorAll("[data-animate]")
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    fetchAvailableYears();
  }, []);

  useEffect(() => {
    if (selectedYear) fetchMembers(selectedYear);
  }, [selectedYear]);

  const fetchAvailableYears = async () => {
    try {
      console.log("Fetching available years from Firebase...");
      const snapshot = await setDoc(
        doc(db, "executiveCommittee", selectedYear),
        {
          createdAt: new Date().toISOString(),
        }
      );

      const years = snapshot.docs
        .map((doc) => doc.id)
        .filter((id) => id !== "placeholder") // Filter out placeholder documents
        .sort()
        .reverse();

      console.log("Processed years:", years);
      setAvailableYears(years);

      // Don't auto-set year anymore since admin will type it manually
    } catch (err) {
      console.error("Failed to fetch available years:", err);
    }
  };

  const fetchMembers = async (year) => {
    if (!year) return;
    try {
      setLoading(true);
      const snapshot = await getDocs(
        collection(db, `executiveCommittee/${year}/members`)
      );
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      // Sort by category order, then by order field
      const categoryOrder = {
        board: 0,
        associates: 1,
        heads: 2,
        "co-heads": 3,
      };
      const sortedData = data.sort((a, b) => {
        // First sort by category
        const aCategoryOrder = categoryOrder[a.category] ?? 4;
        const bCategoryOrder = categoryOrder[b.category] ?? 4;
        if (aCategoryOrder !== bCategoryOrder)
          return aCategoryOrder - bCategoryOrder;

        // Then sort by order within category
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        if (a.order !== undefined) return -1;
        if (b.order !== undefined) return 1;

        // Finally sort by name
        return a.name.localeCompare(b.name);
      });

      setMembers(sortedData);
    } catch (err) {
      console.error("Failed to fetch members:", err);
      setSaveStatus("fetch-error");
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const refreshMembers = async (year) => {
    if (!year) return;
    try {
      const snapshot = await getDocs(
        collection(db, `executiveCommittee/${year}/members`)
      );
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      // Sort by category order, then by order field
      const categoryOrder = {
        board: 0,
        associates: 1,
        heads: 2,
        "co-heads": 3,
      };
      const sortedData = data.sort((a, b) => {
        // First sort by category
        const aCategoryOrder = categoryOrder[a.category] ?? 4;
        const bCategoryOrder = categoryOrder[b.category] ?? 4;
        if (aCategoryOrder !== bCategoryOrder)
          return aCategoryOrder - bCategoryOrder;

        // Then sort by order within category
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        if (a.order !== undefined) return -1;
        if (b.order !== undefined) return 1;

        // Finally sort by name
        return a.name.localeCompare(b.name);
      });

      setMembers(sortedData);
    } catch (err) {
      console.error("Failed to refresh members:", err);
      setSaveStatus("fetch-error");
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate year format
    const yearPattern = /^\d{4}-\d{4}$/;
    if (!yearPattern.test(selectedYear)) {
      setSaveStatus("year-format-error");
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }

    if (
      !selectedYear ||
      !form.name ||
      !form.position ||
      !form.photoUrl ||
      !form.category
    ) {
      setSaveStatus("validation-error");
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }

    const memberId =
      editingMemberId || form.name.toLowerCase().replace(/\s+/g, "_");
    const memberRef = doc(
      db,
      `executiveCommittee/${selectedYear}/members`,
      memberId
    );

    // Get current category members to assign order
    const categoryMembers = members.filter((m) => m.category === form.category);
    const memberData = {
      ...form,
      order: editingMemberId
        ? members.find((m) => m.id === editingMemberId)?.order ??
          categoryMembers.length
        : categoryMembers.length,
    };

    try {
      setLoading(true);
      await setDoc(memberRef, memberData);

      // Remove placeholder if it exists
      const placeholderRef = doc(
        db,
        `executiveCommittee/${selectedYear}/members`,
        "placeholder"
      );
      try {
        await deleteDoc(placeholderRef);
      } catch (err) {
        // Placeholder might not exist, ignore error
      }

      // Reset form and editing state
      setForm({ name: "", position: "", photoUrl: "", category: "board" });
      setEditingMemberId(null);

      // Refresh data without additional loading state
      await refreshMembers(selectedYear);

      // Refresh available years to update navbar
      await fetchAvailableYears();

      setSaveStatus("success");
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error("Failed to save member:", err);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this member?")) return;

    try {
      setLoading(true);
      await deleteDoc(
        doc(db, `executiveCommittee/${selectedYear}/members`, id)
      );

      // Refresh data without additional loading state
      await refreshMembers(selectedYear);

      setSaveStatus("deleted");
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error("Failed to delete member:", err);
      setSaveStatus("delete-error");
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (member) => {
    setForm({
      name: member.name,
      position: member.position,
      photoUrl: member.photoUrl,
      category: member.category,
    });
    setEditingMemberId(member.id);
  };

  const resetForm = () => {
    setForm({ name: "", position: "", photoUrl: "", category: "board" });
    setEditingMemberId(null);
  };

  // Drag and Drop Functions (similar to committee management)
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
    e.target.style.opacity = "0.5";
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverItem(index);
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();

    if (draggedItem === null || draggedItem === dropIndex) return;

    const newMembers = [...members];
    const draggedMember = newMembers[draggedItem];
    const targetMember = newMembers[dropIndex];

    // Only allow reordering within the same category
    if (draggedMember.category !== targetMember.category) {
      setSaveStatus("category-error");
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }

    // Remove dragged item
    newMembers.splice(draggedItem, 1);

    // Insert at new position
    const insertIndex = draggedItem < dropIndex ? dropIndex - 1 : dropIndex;
    newMembers.splice(insertIndex, 0, draggedMember);

    // Update order for members in the same category
    const categoryMembers = newMembers.filter(
      (m) => m.category === draggedMember.category
    );
    const updatedCategoryMembers = categoryMembers.map((member, index) => ({
      ...member,
      order: index,
    }));

    // Update the full members array
    const updatedMembers = newMembers.map((member) => {
      const updatedMember = updatedCategoryMembers.find(
        (cm) => cm.id === member.id
      );
      return updatedMember || member;
    });

    setMembers(updatedMembers);
    await updateMemberOrder(updatedCategoryMembers);
  };

  const updateMemberOrder = async (updatedMembers) => {
    try {
      setIsReordering(true);
      const batch = writeBatch(db);

      updatedMembers.forEach((member) => {
        const memberRef = doc(
          db,
          `executiveCommittee/${selectedYear}/members`,
          member.id
        );
        batch.update(memberRef, { order: member.order });
      });

      await batch.commit();
      setSaveStatus("reordered");
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error("Failed to update order:", err);
      setSaveStatus("reorder-error");
      setTimeout(() => setSaveStatus(null), 3000);
      fetchMembers(selectedYear);
    } finally {
      setIsReordering(false);
    }
  };

  const moveMember = async (index, direction) => {
    const member = members[index];
    const categoryMembers = members.filter(
      (m) => m.category === member.category
    );
    const memberIndexInCategory = categoryMembers.findIndex(
      (m) => m.id === member.id
    );

    const newIndex =
      direction === "up"
        ? memberIndexInCategory - 1
        : memberIndexInCategory + 1;
    if (newIndex < 0 || newIndex >= categoryMembers.length) return;

    const newCategoryMembers = [...categoryMembers];
    const temp = newCategoryMembers[memberIndexInCategory];
    newCategoryMembers[memberIndexInCategory] = newCategoryMembers[newIndex];
    newCategoryMembers[newIndex] = temp;

    // Update order
    const updatedCategoryMembers = newCategoryMembers.map((member, idx) => ({
      ...member,
      order: idx,
    }));

    // Update the full members array
    const updatedMembers = members.map((member) => {
      const updatedMember = updatedCategoryMembers.find(
        (cm) => cm.id === member.id
      );
      return updatedMember || member;
    });

    setMembers(updatedMembers);
    await updateMemberOrder(updatedCategoryMembers);
  };

  const getCategoryData = (categoryId) => {
    return categories.find((c) => c.id === categoryId) || categories[0];
  };

  const getMembersByCategory = (categoryId) => {
    return members.filter((m) => m.category === categoryId && !m.isPlaceholder);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Floating Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-200 dark:bg-blue-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-200 dark:bg-purple-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div
          data-animate
          id="header"
          className={`text-center mb-16 transform transition-all duration-700 ${
            visibleSections.has("header")
              ? "translate-y-0 opacity-100"
              : "translate-y-10 opacity-0"
          }`}
        >
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full mb-6 shadow-lg">
            <FaUsers className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-6">
            Executive Committee Management
          </h1>
          <div className="w-32 h-1 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Manage Executive Committee members across different academic years
            with drag-and-drop reordering
          </p>
        </div>

        {/* Status Messages */}
        {saveStatus && (
          <div className="max-w-4xl mx-auto mb-8">
            {saveStatus === "success" && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 flex items-center space-x-3">
                <FaCheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                <p className="text-green-800 dark:text-green-200 font-medium">
                  Member saved successfully!
                </p>
              </div>
            )}
            {saveStatus === "year-created" && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 flex items-center space-x-3">
                <FaCheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <p className="text-blue-800 dark:text-blue-200 font-medium">
                  New academic year created successfully!
                </p>
              </div>
            )}
            {saveStatus === "category-error" && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4 flex items-center space-x-3">
                <FaExclamationTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                <p className="text-yellow-800 dark:text-yellow-200 font-medium">
                  Can only reorder within the same category!
                </p>
              </div>
            )}
            {saveStatus === "year-format-error" && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4 flex items-center space-x-3">
                <FaExclamationTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                <p className="text-yellow-800 dark:text-yellow-200 font-medium">
                  Please enter year in correct format: YYYY-YYYY (e.g.,
                  2024-2025)
                </p>
              </div>
            )}
            {/* Add other status messages as needed */}
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-purple-500 to-blue-500"></div>

              <div className="p-8">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
                    {editingMemberId ? (
                      <FaEdit className="w-8 h-8 text-white" />
                    ) : (
                      <FaPlus className="w-8 h-8 text-white" />
                    )}
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                      {editingMemberId ? "Edit Member" : "Add Member"}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      {editingMemberId
                        ? "Update member information"
                        : "Add a new EC member"}
                    </p>
                  </div>
                </div>

                {/* Year Input Field - Replace the existing year selection section */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">
                      Academic Year <span className="text-red-500">*</span>
                    </label>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Format: 2024-2025
                    </div>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FaCalendarAlt className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="e.g., 2024-2025"
                      value={selectedYear}
                      onChange={(e) => {
                        setSelectedYear(e.target.value);
                        setForm({
                          name: "",
                          position: "",
                          photoUrl: "",
                          category: "board",
                        });
                        setEditingMemberId(null);
                      }}
                      className="w-full pl-12 pr-4 py-4 rounded-xl bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-200 text-gray-900 dark:text-white font-medium"
                      pattern="[0-9]{4}-[0-9]{4}"
                      title="Please enter year in format: YYYY-YYYY (e.g., 2024-2025)"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Enter the academic year in YYYY-YYYY format
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name Field */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <FaUser className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        placeholder="Enter full name"
                        value={form.name}
                        onChange={(e) =>
                          setForm({ ...form, name: e.target.value })
                        }
                        className="w-full pl-12 pr-4 py-4 rounded-xl bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-200 text-gray-900 dark:text-white"
                        required
                      />
                    </div>
                  </div>

                  {/* Position Dropdown Field - Replace the existing position input */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
                      Position <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <FaBriefcase className="h-5 w-5 text-gray-400" />
                      </div>
                      <select
                        value={form.position}
                        onChange={(e) =>
                          setForm({ ...form, position: e.target.value })
                        }
                        className="w-full pl-12 pr-4 py-4 rounded-xl bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-200 text-gray-900 dark:text-white appearance-none"
                        required
                      >
                        <option value="">Select position...</option>
                        <optgroup label="Board Positions">
                          <option value="Secretary">Secretary</option>
                          <option value="Joint Secretary">
                            Joint Secretary
                          </option>
                          <option value="Associate Joint Secretary ">
                           Associate Joint Secretary 
                          </option>
                          <option value="Treasurer">Treasurer</option>
                          <option value="Associate Secretary">
                            Associate Secretary
                          </option>
                          <option value="Associate Treasurer">
                            Associate Treasurer
                          </option>
                          <option value="Associate">Associate</option>
                        </optgroup>
                        <optgroup label="Committee Positions">
                          <option value="Technical Head">Technical Head</option>
                          <option value="Graphics and Design Head">
                            Graphics and Design Head
                          </option>
                          <option value="Public Relations Head">
                            Public Relations Head
                          </option>
                          <option value="Social and Environmental Head">
                            Social and Environmental Head
                          </option>
                          <option value="Editorial Head">Editorial Head</option>
                          <option value="Program Head">Program Head</option>
                          <option value="Publicity Head">Publicity Head</option>
                          <option value="Alumni and Industry Connect Head">
                            Alumni and Industry Connect Head
                          </option>
                          <option value="Technical Co-Head">
                            Technical Co-Head
                          </option>
                          <option value="Graphics and Design Co-Head">
                            Graphics and Design Co-Head
                          </option>
                          <option value="Public Relations Co-Head">
                            Public Relations Co-Head
                          </option>
                          <option value="Social and Environmental Co-Head">
                            Social and Environmental Co-Head
                          </option>
                          <option value="Editorial Co-Head">
                            Editorial Co-Head
                          </option>
                          <option value="Program Co-Head">
                            Program Co-Head
                          </option>
                          <option value="Publicity Co-Head">
                            Publicity Co-Head
                          </option>
                          <option value="Alumni and Industry Connect Co-Head">
                            Alumni and Industry Connect Co-Head
                          </option>
                        </optgroup>
                      </select>
                      <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Category Field */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={form.category}
                      onChange={(e) =>
                        setForm({ ...form, category: e.target.value })
                      }
                      className="w-full p-4 rounded-xl bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-200 text-gray-900 dark:text-white"
                      required
                    >
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Photo URL Field */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
                      Photo URL <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <FaImage className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="url"
                        placeholder="https://example.com/photo.jpg"
                        value={form.photoUrl}
                        onChange={(e) =>
                          setForm({ ...form, photoUrl: e.target.value })
                        }
                        className="w-full pl-12 pr-4 py-4 rounded-xl bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-200 text-gray-900 dark:text-white"
                        required
                      />
                    </div>
                  </div>

                  {/* Image Preview */}
                  {form.photoUrl && (
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
                        Photo Preview
                      </label>
                      <div className="relative group">
                        <img
                          src={form.photoUrl || "/placeholder.svg"}
                          alt="Preview"
                          className="w-full h-48 object-cover rounded-xl border-2 border-gray-200 dark:border-gray-600 shadow-lg"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                          onLoad={(e) => {
                            e.target.style.display = "block";
                            e.target.nextSibling.style.display = "none";
                          }}
                        />
                        <div className="w-full h-48 border-2 border-dashed border-red-300 dark:border-red-600 rounded-xl flex items-center justify-center bg-red-50 dark:bg-red-900/20 hidden">
                          <div className="text-center">
                            <FaExclamationTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                            <p className="text-sm text-red-500 dark:text-red-400 font-medium">
                              Failed to load image
                            </p>
                          </div>
                        </div>
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl flex items-center justify-center">
                          <FaEye className="w-8 h-8 text-white" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex space-x-4 pt-4">
                    <button
                      type="submit"
                      disabled={loading || !selectedYear}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:shadow-xl disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none flex items-center justify-center space-x-3 shadow-lg"
                    >
                      {loading ? (
                        <>
                          <FaSpinner className="w-5 h-5 animate-spin" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <FaSave className="w-5 h-5" />
                          <span>
                            {editingMemberId ? "Update Member" : "Add Member"}
                          </span>
                        </>
                      )}
                    </button>

                    {editingMemberId && (
                      <button
                        type="button"
                        onClick={resetForm}
                        className="px-6 py-4 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 flex items-center space-x-2 font-medium"
                      >
                        <FaUndo className="w-4 h-4" />
                        <span>Cancel</span>
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Members List Section */}
          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-500"></div>

              <div className="p-8">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <FaUsers className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Executive Committee
                      </h2>
                      {selectedYear && (
                        <p className="text-gray-600 dark:text-gray-400 font-medium">
                          Academic Year: {selectedYear}
                        </p>
                      )}
                    </div>
                  </div>
                  {members.filter((m) => !m.isPlaceholder).length > 0 && (
                    <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-full">
                      <span className="text-sm font-bold text-gray-700 dark:text-gray-300">
                        {members.filter((m) => !m.isPlaceholder).length} member
                        {members.filter((m) => !m.isPlaceholder).length !== 1
                          ? "s"
                          : ""}
                      </span>
                    </div>
                  )}
                </div>

                {!selectedYear ? (
                  <div className="text-center py-20">
                    <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                      <FaCalendarAlt className="w-12 h-12 text-gray-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                      Select Academic Year
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-lg">
                      Choose an academic year to view and manage EC members
                    </p>
                  </div>
                ) : loading ? (
                  <div className="text-center py-20">
                    <FaSpinner className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-6" />
                    <p className="text-xl text-gray-600 dark:text-gray-400 font-medium">
                      Loading members...
                    </p>
                  </div>
                ) : members.filter((m) => !m.isPlaceholder).length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                      <FaUsers className="w-12 h-12 text-gray-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                      No Members Yet
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-lg">
                      Add the first member to this academic year using the form
                    </p>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {categories.map((category) => {
                      const categoryMembers = getMembersByCategory(category.id);
                      if (categoryMembers.length === 0) return null;

                      return (
                        <div key={category.id} className="space-y-4">
                          <div className="flex items-center space-x-3">
                            <div
                              className={`w-4 h-4 bg-gradient-to-r ${category.color} rounded-full`}
                            ></div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                              {category.name}
                            </h3>
                            <div className="bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                {categoryMembers.length}
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {categoryMembers.map((member, index) => {
                              const globalIndex = members.findIndex(
                                (m) => m.id === member.id
                              );
                              const categoryIndex = categoryMembers.findIndex(
                                (m) => m.id === member.id
                              );

                              return (
                                <div
                                  key={member.id}
                                  draggable
                                  onDragStart={(e) =>
                                    handleDragStart(e, globalIndex)
                                  }
                                  onDragEnd={handleDragEnd}
                                  onDragOver={(e) =>
                                    handleDragOver(e, globalIndex)
                                  }
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) => handleDrop(e, globalIndex)}
                                  className={`group bg-gray-50 dark:bg-gray-700 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 border-2 cursor-move ${
                                    dragOverItem === globalIndex
                                      ? "border-purple-400 dark:border-purple-500 bg-purple-50 dark:bg-purple-900/20"
                                      : "border-gray-200 dark:border-gray-600"
                                  } ${
                                    draggedItem === globalIndex
                                      ? "opacity-50 scale-95"
                                      : ""
                                  }`}
                                >
                                  <div className="p-4">
                                    <div className="flex items-center space-x-3">
                                      {/* Drag Handle */}
                                      <div className="flex flex-col items-center space-y-1">
                                        <FaGripVertical className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors duration-200" />
                                        <div className="flex flex-col space-y-1">
                                          <button
                                            onClick={() =>
                                              moveMember(globalIndex, "up")
                                            }
                                            disabled={
                                              categoryIndex === 0 ||
                                              isReordering
                                            }
                                            className="p-1 text-gray-400 hover:text-purple-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200"
                                            title="Move up"
                                          >
                                            <FaArrowUp className="w-2 h-2" />
                                          </button>
                                          <button
                                            onClick={() =>
                                              moveMember(globalIndex, "down")
                                            }
                                            disabled={
                                              categoryIndex ===
                                                categoryMembers.length - 1 ||
                                              isReordering
                                            }
                                            className="p-1 text-gray-400 hover:text-purple-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200"
                                            title="Move down"
                                          >
                                            <FaArrowDown className="w-2 h-2" />
                                          </button>
                                        </div>
                                      </div>

                                      {/* Member Photo */}
                                      <div className="relative">
                                        <img
                                          src={
                                            member.photoUrl ||
                                            "/placeholder.svg"
                                          }
                                          alt={member.name}
                                          className="w-12 h-12 object-cover rounded-full border-2 border-white dark:border-gray-600 shadow-md"
                                          onError={(e) => {
                                            e.target.src = "/placeholder.svg";
                                          }}
                                        />
                                      </div>

                                      {/* Member Info */}
                                      <div className="flex-1 min-w-0">
                                        <h4 className="text-sm font-bold text-gray-900 dark:text-white truncate group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-200">
                                          {member.name}
                                        </h4>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                          {member.position}
                                        </p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500">
                                          #{categoryIndex + 1} in{" "}
                                          {category.name}
                                        </p>
                                      </div>

                                      {/* Action Buttons */}
                                      <div className="flex space-x-1">
                                        <button
                                          onClick={() => handleEdit(member)}
                                          className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-xs"
                                        >
                                          <FaEdit className="w-3 h-3" />
                                        </button>
                                        <button
                                          onClick={() =>
                                            handleDelete(member.id)
                                          }
                                          className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-xs"
                                        >
                                          <FaTrash className="w-3 h-3" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
};

export default ECUpdate;
